import { Component, type ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort catch for render-phase errors (e.g. a Firebase client that
 * throws synchronously on init with a bad/missing config) so a single
 * misconfigured dependency degrades to a message instead of a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // eslint-disable-next-line no-console
    console.error('Uncaught render error:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-4 bg-void px-6 text-center">
          <p className="font-display text-2xl text-text">Something didn't load right.</p>
          <p className="max-w-md text-sm text-muted">
            Refreshing usually fixes it. If it keeps happening, tell us at{' '}
            <a className="text-dawn underline" href="mailto:hello@everyonestomorrow.in">
              hello@everyonestomorrow.in
            </a>
            .
          </p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
